import { z } from 'zod';
import { invoiceSchema } from '@/lib/schemas';
import { Eye, Download, DollarSign, Trash2 } from 'lucide-react';

type Invoice = z.infer<typeof invoiceSchema>;

export interface InvoiceActionContext {
  navigate: (path: string) => void;
}

export interface InvoiceActionDef {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  visible: (invoice: Invoice) => boolean;
  execute: (invoice: Invoice, ctx: InvoiceActionContext) => void;
}

/**
 * Phase 10a.9: Action registry for invoice views.
 * All actions in this batch navigate to the invoice detail page.
 */
export const INVOICE_ACTION_REGISTRY: Record<string, InvoiceActionDef> = {
  navigate_detail: {
    label: 'View',
    icon: Eye,
    visible: () => true,
    execute: (invoice, ctx) => {
      ctx.navigate(`/invoices/${invoice.id}`);
    },
  },

  record_payment: {
    label: 'Record Payment',
    icon: DollarSign,
    visible: (invoice) => invoice.status === 'sent' || invoice.status === 'partially_paid',
    execute: (invoice, ctx) => {
      ctx.navigate(`/invoices/${invoice.id}`);
    },
  },

  download_pdf: {
    label: 'Download PDF',
    icon: Download,
    visible: () => true,
    execute: (invoice, ctx) => {
      ctx.navigate(`/invoices/${invoice.id}`);
    },
  },

  delete: {
    label: 'Delete',
    icon: Trash2,
    visible: (invoice) => invoice.status === 'draft',
    execute: (invoice, ctx) => {
      ctx.navigate(`/invoices/${invoice.id}`);
    },
  },
};

/**
 * Resolve config action keys to ordered InvoiceActionDef[].
 * Silently skips unknown keys for forward compatibility.
 */
export function resolveInvoiceActions(actionKeys: string[]): { key: string; def: InvoiceActionDef }[] {
  return actionKeys
    .filter((key) => key in INVOICE_ACTION_REGISTRY)
    .map((key) => ({ key, def: INVOICE_ACTION_REGISTRY[key] }));
}
